import { OffsetMap } from '../types/offsetMap'

/**
 * Calculate cumulative offset from one frame to another
 * by chaining sequential offsets from the offset map.
 * 
 * Example:
 * - To go from frame 4 to frame 7:
 *   offset(4→7) = offset(4→5) + offset(5→6) + offset(6→7)
 * 
 * @param fromFrame - Starting frame index
 * @param toFrame - Target frame index
 * @param offsetMap - Pre-calculated offset map with sequential frame pairs
 * @returns Cumulative offset {x, y} in pixels, or null if path not available
 */
export function getCumulativeOffset(
  fromFrame: number,
  toFrame: number,
  offsetMap: OffsetMap | null
): { x: number; y: number } | null {
  // No offset if same frame
  if (fromFrame === toFrame) {
    return { x: 0, y: 0 }
  }

  // No offset map available
  if (!offsetMap) {
    console.warn(`[getCumulativeOffset] No offset map available`)
    return null
  }

  // Determine direction (forward or backward)
  const direction = toFrame > fromFrame ? 1 : -1
  const start = fromFrame
  const end = toFrame

  let cumulativeX = 0
  let cumulativeY = 0

  // Chain sequential offsets
  for (let i = start; direction > 0 ? i < end : i > end; i += direction) {
    const currentFrame = i
    const nextFrame = i + direction

    // Create offset key (always smaller→larger for lookup)
    const key = direction > 0 
      ? `${currentFrame}_${nextFrame}` 
      : `${nextFrame}_${currentFrame}`

    // Try all_pairs first, then fall back to sequential
    const offset = offsetMap.all_pairs?.[key] || offsetMap.sequential?.[key]

    if (!offset) {
      console.warn(
        `[getCumulativeOffset] Missing offset for frames ${currentFrame}→${nextFrame} (key: ${key})`
      )
      return null
    }

    // Add offset (negate if going backward)
    if (direction > 0) {
      cumulativeX += offset.dx
      cumulativeY += offset.dy
    } else {
      cumulativeX -= offset.dx
      cumulativeY -= offset.dy
    }
  }

  return { x: cumulativeX, y: cumulativeY }
}

/**
 * Calculate offsets for a range of frames relative to a current frame.
 * 
 * @param currentFrame - Reference frame (gets offset {0, 0})
 * @param frameIndices - Array of frame indices to calculate offsets for
 * @param offsetMap - Pre-calculated offset map
 * @returns Map of frame index → offset {x, y}, excludes frames with no path
 */
export function calculateFrameOffsets(
  currentFrame: number,
  frameIndices: number[],
  offsetMap: OffsetMap | null
): Map<number, { x: number; y: number }> {
  const offsets = new Map<number, { x: number; y: number }>()

  for (const frameIndex of frameIndices) {
    const offset = getCumulativeOffset(currentFrame, frameIndex, offsetMap)
    
    if (offset) {
      offsets.set(frameIndex, offset)
    } else {
      console.warn(
        `[calculateFrameOffsets] Skipping frame ${frameIndex} (no path from ${currentFrame})`
      )
    }
  }

  return offsets
}

/**
 * Get a range of frame indices around a current frame.
 * 
 * @param currentFrame - Center frame
 * @param range - Number of frames before and after (e.g., 2 = ±2 = 5 frames total)
 * @param totalFrames - Total number of frames available (for bounds checking)
 * @returns Array of frame indices in range [current-range, current+range]
 */
export function getFrameRange(
  currentFrame: number,
  range: number,
  totalFrames: number
): number[] {
  const indices: number[] = []
  
  const start = Math.max(0, currentFrame - range)
  const end = Math.min(totalFrames - 1, currentFrame + range)
  
  for (let i = start; i <= end; i++) {
    indices.push(i)
  }
  
  return indices
}

/**
 * Calculate cumulative depth change (dz) from one frame to another.
 * 
 * This function chains sequential dz values to determine total depth change.
 * Positive dz means camera moved closer (objects appear larger).
 * Negative dz means camera moved farther (objects appear smaller).
 * 
 * Example:
 * - Frame 0→5: dz values [0.02, 0.03, 0.04, 0.02, 0.03] → cumulative: +0.14 (14% closer)
 * - Frame 10→7: Going backward, so negate: -0.15 (-15% farther)
 * 
 * Use cases:
 * - Detect scene changes (large |dz| = different scene)
 * - Find zoom frames (target specific dz for zoom in/out)
 * - Filter pan navigation (avoid frames with large depth jumps)
 * 
 * @param fromFrame - Starting frame index
 * @param toFrame - Target frame index
 * @param offsetMap - Pre-calculated offset map with dz values
 * @returns Cumulative dz (scale factor deviation from 1.0), or null if path not available
 * 
 * @example
 * ```typescript
 * // Check depth change from frame 5 to frame 10
 * const dz = getCumulativeDz(5, 10, offsetMap)
 * if (dz && dz > 0.20) {
 *   console.log("Camera moved 20% closer - objects are larger")
 * }
 * ```
 */
export function getCumulativeDz(
  fromFrame: number,
  toFrame: number,
  offsetMap: OffsetMap | null
): number | null {
  // No depth change if same frame
  if (fromFrame === toFrame) {
    return 0
  }

  // No offset map available
  if (!offsetMap) {
    console.warn(`[getCumulativeDz] No offset map available`)
    return null
  }

  // Determine direction (forward or backward)
  const direction = toFrame > fromFrame ? 1 : -1

  let cumulativeDz = 0

  // Chain sequential dz values
  for (let i = fromFrame; direction > 0 ? i < toFrame : i > toFrame; i += direction) {
    const currentFrame = i
    const nextFrame = i + direction

    // Create offset key (always smaller→larger for lookup)
    const key = direction > 0 
      ? `${currentFrame}_${nextFrame}` 
      : `${nextFrame}_${currentFrame}`

    // Try all_pairs first, then fall back to sequential
    const offset = offsetMap.all_pairs?.[key] || offsetMap.sequential?.[key]

    if (!offset) {
      console.warn(
        `[getCumulativeDz] Missing offset for frames ${currentFrame}→${nextFrame} (key: ${key})`
      )
      return null
    }

    // Add dz (negate if going backward)
    if (direction > 0) {
      cumulativeDz += offset.dz
    } else {
      cumulativeDz -= offset.dz
    }
  }

  console.log(
    `[getCumulativeDz] Frame ${fromFrame}→${toFrame}: dz = ${cumulativeDz > 0 ? '+' : ''}${cumulativeDz.toFixed(4)} (${(cumulativeDz * 100).toFixed(1)}% depth change)`
  )

  return cumulativeDz
}

/**
 * Check if the depth change between two frames exceeds a threshold.
 * 
 * This is useful for filtering out frames with large depth changes during
 * navigation, as they often indicate scene changes or viewing position shifts
 * rather than simple zoom/pan within the same scene.
 * 
 * Based on analysis of actual data:
 * - Sequential frames typically have |dz| < 0.08 (8%)
 * - Scene changes usually have |dz| > 0.10 (10%)
 * - Recommended threshold: 0.08 for safety
 * 
 * @param fromFrame - Starting frame index
 * @param toFrame - Target frame index
 * @param offsetMap - Pre-calculated offset map with dz values
 * @param threshold - Maximum allowed |dz| (default: 0.08 = 8%)
 * @returns true if depth change exceeds threshold, false otherwise
 * 
 * @example
 * ```typescript
 * // Filter out frames with large depth changes
 * if (isLargeDzChange(currentFrame, candidateFrame, offsetMap, 0.08)) {
 *   console.log("Skipping frame - too much depth change (likely different scene)")
 *   continue
 * }
 * ```
 */
export function isLargeDzChange(
  fromFrame: number,
  toFrame: number,
  offsetMap: OffsetMap | null,
  threshold: number = 0.08
): boolean {
  const cumulativeDz = getCumulativeDz(fromFrame, toFrame, offsetMap)
  
  // If we can't calculate dz (no path), don't filter it out
  if (cumulativeDz === null) {
    return false
  }
  
  const isLarge = Math.abs(cumulativeDz) > threshold
  
  if (isLarge) {
    console.log(
      `[isLargeDzChange] Frame ${fromFrame}→${toFrame}: |dz| = ${Math.abs(cumulativeDz).toFixed(4)} > threshold ${threshold} ⚠️`
    )
  }
  
  return isLarge
}
